import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import {
  LayoutDashboard, Server, Database, Globe, ListOrdered, ScrollText, BellRing,
  Users as UsersIcon, Settings as SettingsIcon, LogOut, ChevronDown, Sun, Moon, Key, ShieldAlert
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Genel Bakış", icon: LayoutDashboard, exact: true, testid: "nav-dashboard" },
  { to: "/whm", label: "WHM Sunucuları", icon: Server, testid: "nav-whm" },
  { to: "/pdns", label: "PowerDNS Sunucuları", icon: Database, testid: "nav-pdns" },
  { to: "/zones", label: "Zone'lar", icon: Globe, testid: "nav-zones" },
  { to: "/queue", label: "Senkron Kuyruğu", icon: ListOrdered, testid: "nav-queue" },
  { to: "/events", label: "Olay Günlüğü", icon: ScrollText, testid: "nav-events" },
  { to: "/alerts", label: "Bildirimler", icon: BellRing, testid: "nav-alerts" },
  { to: "/content", label: "İçerik Uyarıları", icon: ShieldAlert, testid: "nav-content" },
  { to: "/park", label: "API & Park", icon: Key, testid: "nav-apikeys", super: true },
  { to: "/users", label: "Kullanıcılar", icon: UsersIcon, testid: "nav-users", super: true },
  { to: "/settings", label: "Ayarlar", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-background noise-bg">
      <aside className="fixed left-0 top-0 h-screen w-64 border-r border-border bg-card z-40 flex flex-col">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-border">
          <img
            src="https://customer-assets.emergentagent.com/job_dns-orchestrator/artifacts/qxf4tj1j_netlen-white.svg"
            alt="Netlen"
            className="h-7 w-auto"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div className="flex flex-col leading-tight">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">Netlen</div>
            <div className="text-sm font-semibold">DNS Paneli</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {NAV.filter((item) => !item.super || user?.role === "superadmin").map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) => `sidebar-item relative ${isActive ? "active" : ""}`}
              data-testid={item.testid}
            >
              <item.icon size={16} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-border text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          v1.0.0 · panel
        </div>
      </aside>

      <div className="pl-64">
        <header className="h-16 sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-md flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Netlen / DNS Orchestrator</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-9 w-9"
              title={theme === "dark" ? "Açık temaya geç" : "Koyu temaya geç"}
              data-testid="theme-toggle"
              aria-label="Tema değiştir"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 h-9" data-testid="header-user-menu">
                  <div className="h-7 w-7 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center text-xs font-semibold text-primary">
                    {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <span className="text-sm">{user?.name || user?.email}</span>
                  <ChevronDown size={14} className="text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-xs text-muted-foreground font-mono">{user?.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-400 focus:text-red-400">
                  <LogOut size={14} className="mr-2" /> Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="p-6 animate-fade-in">{children}</main>
      </div>
    </div>
  );
}
